import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { isXlsxBuffer, isLegacyXlsBuffer, leadsToMarkup, decodeText } from '../DocumentViewer/spreadsheetBytes';
import { BorderCellEntry, BorderInfoEntry, BorderSideValue, FortuneCell, FortuneCellData, FortuneSheet } from './types';
import { MIN_ROW_HEIGHT, MIN_ROWS, MIN_COLS } from './fileTypes';
import { aggregateBorderInfo } from './borderAggregation';

// Returns the SSF format string for a cell, or 'General' when absent/generic.
const numFmt = (z?: string | number): string => (typeof z === 'string' && z !== 'General' ? z : 'General');

// SSF-unsupported format strings (e.g. accounting-style percents such as
// `#,##0%;\(#,##0\)%;\-`, where the `%` trails a literal `)` instead of sitting next
// to a digit placeholder) still mean "multiply by 100 and show a %" even though
// XLSX.SSF.format can't parse them — degrade to a plain percentage instead of the raw
// unmultiplied fraction (e.g. "-3.39%" rather than "-0.033933439679959716").
const percentFallback = (value: number): string => `${(value * 100).toFixed(2)}%`;

// SSF.format throws on some formats exceljs reads verbatim (accounting/locale); such a
// format must not reach ct.fa, where Fortune-Sheet would re-throw on it later.
const isFormattableNumFmt = (fmt: string): boolean => {
    try {
        // One probe per section: a format can parse for positives yet throw on negatives.
        [1, -1, 0].forEach((probe) => XLSX.SSF.format(fmt, probe));
        return true;
    } catch {
        return false;
    }
};

// Memoises isFormattableNumFmt by format string — a column's format repeats
// across every cell, so without this a large sheet pays up to 3 SSF.format
// probes per cell instead of per distinct format.
const formattableNumFmtCache = new Map<string, boolean>();
// Caps the cache's lifetime memory: a long-lived page session that opens many
// distinct number formats across many workbooks would otherwise retain every
// one forever. Simplest correct bound — a full clear on overflow, not an LRU.
const FORMATTABLE_NUM_FMT_CACHE_MAX = 500;
const safeNumFmt = (z?: string | number): string => {
    const fmt = numFmt(z);
    if (fmt === 'General') return fmt;
    let formattable = formattableNumFmtCache.get(fmt);
    if (formattable === undefined) {
        formattable = isFormattableNumFmt(fmt);
        if (formattableNumFmtCache.size >= FORMATTABLE_NUM_FMT_CACHE_MAX) formattableNumFmtCache.clear();
        formattableNumFmtCache.set(fmt, formattable);
    }
    return formattable ? fmt : 'General';
};

// Google-Sheets / Excel-style default horizontal alignment by cell type, used
// only when the source carried no explicit alignment. Fortune-Sheet has no
// type-based auto-align — an unset `ht` renders LEFT for every type — so numbers
// would otherwise import left-aligned. ht codes: 0 center, 1 left, 2 right.
// Numbers and dates (dates import as numeric serials, ct.t === 'n') → right;
// booleans and errors → center; text → left (left unset, so we return undefined).
const defaultHtForType = (t?: string): 0 | 2 | undefined => {
    if (t === 'n') return 2;
    if (t === 'b' || t === 'e') return 0;
    return undefined;
};

/**
 * Convert one SheetJS worksheet into Fortune-Sheet celldata, preserving formulas.
 * Serves the CSV path and the legacy .xls path (parseLegacyXls, below) — only
 * .xlsx import/export goes through exceljs (further below) for full cell-style
 * fidelity (SheetJS's community build can't read/write cell.s).
 * Exported for unit tests.
 */
export const worksheetToCelldata = (
    ws: XLSX.WorkSheet,
): { celldata: FortuneCellData[]; rows: number; cols: number; config: FortuneSheet['config'] } => {
    const celldata: FortuneCellData[] = [];
    const ref = ws['!ref'];
    if (!ref) return { celldata, rows: MIN_ROWS, cols: MIN_COLS, config: {} };

    const range = XLSX.utils.decode_range(ref);
    let realMaxR = 0;
    let realMaxC = 0;

    const config: NonNullable<FortuneSheet['config']> = {};
    // FortuneSheet renders a merge's rowspan/colspan off the CELL's own `mc` field, not
    // config.merge (see exceljsWorksheetToCelldata's own comment below) — built BEFORE
    // the main loop so every cell in a merge range (including ones the loop would
    // otherwise skip for carrying no value) can be given its `mc`.
    const masterMc = new Map<string, MergeMaster>();
    const coveredMc = new Map<string, { r: number; c: number }>();
    const merges = ws['!merges'];
    if (merges?.length) {
        config.merge = {};
        merges.forEach((m) => {
            const master: MergeMaster = { r: m.s.r, c: m.s.c, rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 };
            config.merge![`${m.s.r}_${m.s.c}`] = master;
            masterMc.set(`${m.s.r}_${m.s.c}`, master);
            // A merge can reach past the last valued cell — extend the sheet bounds to fit.
            if (m.e.r > realMaxR) realMaxR = m.e.r;
            if (m.e.c > realMaxC) realMaxC = m.e.c;
            for (let rr = m.s.r; rr <= m.e.r; rr++) {
                for (let cc = m.s.c; cc <= m.e.c; cc++) {
                    if (rr === m.s.r && cc === m.s.c) continue;
                    coveredMc.set(`${rr}_${cc}`, { r: m.s.r, c: m.s.c });
                }
            }
        });
    }

    const emitted = new Set<string>();

    // Iterate only the cells that actually exist — looping the full !ref
    // rectangle explodes to millions of iterations on sparse/templated sheets.
    Object.keys(ws).forEach((addr) => {
        if (addr.startsWith('!')) return;
        const { r, c } = XLSX.utils.decode_cell(addr);
        const key = `${r}_${c}`;

        // A covered (non-master) merge cell. Unlike exceljs, SheetJS does NOT proxy the
        // master's value onto it — it carries its own (usually blank) cell record — but
        // that record's own content must still never reach the sheet; only `mc` does.
        const covered = coveredMc.get(key);
        if (covered) {
            celldata.push({ r, c, v: { mc: covered } });
            emitted.add(key);
            if (r > realMaxR) realMaxR = r;
            if (c > realMaxC) realMaxC = c;
            return;
        }

        const cell = ws[addr] as XLSX.CellObject;
        const fcell: FortuneCell = {};
        // SheetJS stores formulas without the leading '='; Fortune-Sheet wants it.
        if (cell.f) fcell.f = `=${cell.f}`;

        if (cell.t === 'n') {
            fcell.v = cell.v as number;
            fcell.m = cell.w ?? String(cell.v);
            fcell.ct = { t: 'n', fa: safeNumFmt(cell.z) };
        } else if (cell.t === 'b') {
            fcell.v = cell.v as boolean;
            fcell.m = cell.w ?? (cell.v ? 'TRUE' : 'FALSE');
            fcell.ct = { t: 'b', fa: 'General' };
        } else if (cell.t === 'e') {
            fcell.v = (cell.w as string) ?? String(cell.v);
            fcell.m = fcell.v as string;
            fcell.ct = { t: 'e', fa: 'General' };
        } else if (cell.v !== undefined && cell.v !== null) {
            fcell.v = cell.v == null ? '' : String(cell.v);
            fcell.m = cell.w ?? String(fcell.v);
            fcell.ct = { t: 's', fa: '@' };
            fcell.tb = '1';
        }

        if (fcell.v === undefined && !fcell.f) return;
        const ht = defaultHtForType(fcell.ct?.t);
        if (ht !== undefined) fcell.ht = ht;
        const master = masterMc.get(key);
        if (master) fcell.mc = master;
        celldata.push({ r, c, v: fcell });
        emitted.add(key);
        if (r > realMaxR) realMaxR = r;
        if (c > realMaxC) realMaxC = c;
    });

    // Backfill merge master/covered cells the main loop never visited — an
    // entirely empty merged block carries no cell record at all for SheetJS to iterate.
    masterMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        celldata.push({ r: master.r, c: master.c, v: { mc: master } });
    });
    coveredMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        const [r, c] = key.split('_').map(Number);
        celldata.push({ r, c, v: { mc: master } });
    });

    const cols = ws['!cols'];
    if (cols?.length) {
        config.columnlen = {};
        cols.forEach((col, i) => {
            if (!col) return;
            const px = col.wpx ?? (col.wch != null ? Math.round(col.wch * 7 + 5) : undefined);
            if (px != null) config.columnlen![String(i)] = px;
        });
    }
    const rowsCfg = ws['!rows'];
    if (rowsCfg?.length) {
        config.rowlen = {};
        rowsCfg.forEach((row, i) => {
            if (!row) return;
            const px = row.hpx ?? (row.hpt != null ? Math.round((row.hpt * 96) / 72) : undefined);
            // Clamp UP to the floor — a shorter authored row height (e.g. from a
            // dense source workbook) must not render shorter than MIN_ROW_HEIGHT.
            if (px != null) config.rowlen![String(i)] = Math.max(px, MIN_ROW_HEIGHT);
        });
    }

    // The declared `!ref` extent can vastly overstate the real cell extent
    // (e.g. a template's `A1:Z1048576` with only a handful of populated
    // cells) — Fortune-Sheet allocates a dense matrix sized off `rows`/`cols`,
    // so clamp to whichever is smaller rather than trusting the declaration.
    return {
        celldata,
        rows: Math.max(MIN_ROWS, Math.min(range.e.r + 1, realMaxR + 1)),
        cols: Math.max(MIN_COLS, Math.min(range.e.c + 1, realMaxC + 1)),
        config,
    };
};

/** Fortune only tracks a formula for recalculation once it is in the sheet's
 *  `calcChain` ({r, c, id}); it adds entries only when it evaluates a formula
 *  itself, so an imported formula (cached value, no calcChain) would never
 *  recompute when its precedents change. */
const buildCalcChain = (celldata: FortuneCellData[], id: string): { r: number; c: number; id: string }[] =>
    celldata.filter((cell) => cell.v?.f).map(({ r, c }) => ({ r, c, id }));

const buildSheet = (
    name: string,
    index: number,
    celldata: FortuneCellData[],
    rows: number,
    cols: number,
    config?: FortuneSheet['config'],
    defaultRowHeight?: number,
    defaultColWidth?: number,
): FortuneSheet => {
    const calcChain = buildCalcChain(celldata, String(index));
    return {
        name,
        id: String(index),
        order: index,
        status: index === 0 ? 1 : 0,
        celldata,
        ...(calcChain.length ? { calcChain } : {}),
        row: rows,
        column: cols,
        ...(config && Object.keys(config).length ? { config } : {}),
        ...(defaultRowHeight != null ? { defaultRowHeight } : {}),
        ...(defaultColWidth != null ? { defaultColWidth } : {}),
    };
};

const parseCsv = (text: string): FortuneSheet[] => {
    // Capture raw CSV tokens via PapaParse BEFORE SheetJS numeric coercion.
    // XLSX.read converts 17+ digit values to rounded JS floats, making cell.w
    // unreliable for recovering the original token; the raw PapaParse strings are exact.
    const rawByAddr = new Map<string, string>();
    Papa.parse<string[]>(text, { skipEmptyLines: false }).data.forEach((row, r) => {
        row.forEach((rawVal, c) => {
            if (rawVal !== '' && rawVal != null) {
                rawByAddr.set(XLSX.utils.encode_cell({ r, c }), rawVal);
            }
        });
    });

    // Parse CSV with the same engine as xlsx so numbers, dates and formats are
    // detected identically. `decodeText` (caller) already handled UTF-16/BOM.
    const wb = XLSX.read(text, { type: 'string', cellNF: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (ws) {
        // Keep long numeric IDs (>=16 digits, e.g. account/UTR numbers) as text —
        // a JS double can't represent them exactly, so typing them as numbers corrupts them.
        Object.keys(ws).forEach((addr) => {
            if (addr.startsWith('!')) return;
            const cell = ws[addr] as XLSX.CellObject;
            const raw = rawByAddr.get(addr) ?? '';
            const digits = raw.replace(/[,\s]/g, '');
            if (cell.t === 'n' && /^\d{16,}$/.test(digits)) {
                cell.t = 's';
                cell.v = raw;
                delete cell.z;
            }
        });
    }
    const { celldata, rows, cols, config } = ws
        ? worksheetToCelldata(ws)
        : { celldata: [], rows: MIN_ROWS, cols: MIN_COLS, config: {} as FortuneSheet['config'] };
    return [buildSheet('Sheet1', 0, celldata, rows, cols, config)];
};

/**
 * Parse a legacy binary .xls (BIFF5/BIFF8) or a text-shaped .xls variant
 * (HTML-table, SpreadsheetML-2003) via SheetJS — exceljs is OOXML/ZIP-only
 * and throws on all of these (the bug this function fixes). Unlike parseCsv,
 * maps EVERY sheet: a legacy workbook is commonly multi-sheet.
 * ⚠️ Do NOT pass cellDates: true — worksheetToCelldata has no 'd' branch, so a
 * cellDates date cell would fall through to the string branch and corrupt the
 * cell's value, sort order and any re-saved .xlsx (see worksheetToCelldata's
 * own invariant comment above). cellNF: true is required: it populates
 * cell.z, which worksheetToCelldata reads for ct.fa.
 */
const parseLegacyXls = (buffer: ArrayBuffer): FortuneSheet[] => {
    const wb = XLSX.read(new Uint8Array(buffer), { type: 'array', cellNF: true });
    const sheets = wb.SheetNames.map((name, i) => {
        const { celldata, rows, cols, config } = worksheetToCelldata(wb.Sheets[name]);
        return buildSheet(name, i, celldata, rows, cols, config);
    });
    // A successful-but-empty parse (e.g. a truncated OLE2 file, or an empty
    // <table></table> named .xls) must THROW rather than return [] — an empty
    // array reaches useResolveSheets.ts's emptyFallback and renders a blank
    // 50x26 grid with no error at all, the exact symptom this ticket reports,
    // via a different mechanism. This message is Sentry-only (captured by
    // sheetLoadTelemetry.ts), never rendered to the user, so it carries no
    // parsed content.
    if (sheets.every((sheet) => (sheet.celldata ?? []).length === 0)) {
        throw new Error('Unsupported or unreadable spreadsheet content. Download it to open in Excel.');
    }
    return sheets;
};

// ============================================================================
// .xlsx IMPORT — exceljs (full font/fill/alignment/border read fidelity;
// SheetJS's community build only ever surfaces fill color, see git history).
// ============================================================================

// Border style name <-> Fortune-Sheet/luckysheet numeric code, shared both directions.
const BORDER_STYLE_TO_CODE: Record<string, number> = {
    thin: 1,
    hair: 2,
    dotted: 3,
    dashed: 4,
    dashDot: 5,
    dashDotDot: 6,
    double: 7,
    medium: 8,
    mediumDashed: 9,
    mediumDashDot: 10,
    mediumDashDotDot: 11,
    slantDashDot: 12,
    thick: 13,
};
const BORDER_CODE_TO_STYLE: Record<number, ExcelJS.BorderStyle> = Object.fromEntries(
    Object.entries(BORDER_STYLE_TO_CODE).map(([style, code]) => [code, style as ExcelJS.BorderStyle]),
);

// exceljs surfaces colors three ways: explicit {argb} (8-hex, leading alpha),
// palette {theme, tint} (the standard Excel colour picker — most real coloring),
// and legacy {indexed}. argbToHex handles the first; resolveColor (below) also
// resolves theme colours against the workbook palette so they aren't dropped.
const argbToHex = (argb?: string): string | undefined => (argb ? `#${argb.slice(-6).toUpperCase()}` : undefined);
const hexToArgb = (hex?: string): string | undefined => (hex ? `FF${hex.replace(/^#/, '').slice(-6).toUpperCase()}` : undefined);

// ── Theme colour resolution ────────────────────────────────────────────────
// Excel stores palette colours as an index into the workbook theme plus a tint.
// exceljs exposes the theme XML at wb.model.themes.theme1 but does NOT resolve
// the index/tint to RGB, so without this the standard picker colours vanish.

type ExcelColor = { argb?: string; theme?: number; tint?: number; indexed?: number };

// clrScheme declaration order in theme1.xml.
const THEME_SCHEME_NAMES = [
    'dk1',
    'lt1',
    'dk2',
    'lt2',
    'accent1',
    'accent2',
    'accent3',
    'accent4',
    'accent5',
    'accent6',
    'hlink',
    'folHlink',
] as const;
// Standard Office palette in SpreadsheetML theme-index order — the fallback when
// a workbook ships no theme1 or an entry can't be parsed.
const OFFICE_THEME_PALETTE = [
    '#FFFFFF',
    '#000000',
    '#E7E6E6',
    '#44546A',
    '#4472C4',
    '#ED7D31',
    '#A5A5A5',
    '#FFC000',
    '#5B9BD5',
    '#70AD47',
    '#0563C1',
    '#954F72',
];

/**
 * Parse the 12 theme colours out of theme1.xml into **SpreadsheetML theme-index
 * order**. That order swaps the first two pairs relative to the clrScheme
 * declaration order — index 0=lt1, 1=dk1, 2=lt2, 3=dk2 (then accent1-6, hlink,
 * folHlink) — the classic OOXML gotcha. Missing entries fall back to Office.
 * Exported for unit tests.
 */
export const parseThemePalette = (themeXml?: string): string[] => {
    const map: Record<string, string> = {};
    if (themeXml) {
        const scheme = (themeXml.match(/<a:clrScheme[\s\S]*?<\/a:clrScheme>/) || [''])[0];
        THEME_SCHEME_NAMES.forEach((name) => {
            const block = (scheme.match(new RegExp(`<a:${name}>([\\s\\S]*?)</a:${name}>`)) || ['', ''])[1];
            // Prefer an explicit srgbClr; else a sysClr's lastClr (e.g. windowText → 000000).
            const hex =
                (block.match(/<a:srgbClr\s+val="([0-9A-Fa-f]{6})"/) || [])[1] || (block.match(/lastClr="([0-9A-Fa-f]{6})"/) || [])[1];
            if (hex) map[name] = `#${hex.toUpperCase()}`;
        });
    }
    const pick = (name: string, fallback: string) => map[name] ?? fallback;
    return [
        pick('lt1', OFFICE_THEME_PALETTE[0]),
        pick('dk1', OFFICE_THEME_PALETTE[1]),
        pick('lt2', OFFICE_THEME_PALETTE[2]),
        pick('dk2', OFFICE_THEME_PALETTE[3]),
        pick('accent1', OFFICE_THEME_PALETTE[4]),
        pick('accent2', OFFICE_THEME_PALETTE[5]),
        pick('accent3', OFFICE_THEME_PALETTE[6]),
        pick('accent4', OFFICE_THEME_PALETTE[7]),
        pick('accent5', OFFICE_THEME_PALETTE[8]),
        pick('accent6', OFFICE_THEME_PALETTE[9]),
        pick('hlink', OFFICE_THEME_PALETTE[10]),
        pick('folHlink', OFFICE_THEME_PALETTE[11]),
    ];
};

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h / 6, s, l];
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    if (s === 0) {
        const v = Math.round(l * 255);
        return [v, v, v];
    }
    const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [Math.round(hue2rgb(p, q, h + 1 / 3) * 255), Math.round(hue2rgb(p, q, h) * 255), Math.round(hue2rgb(p, q, h - 1 / 3) * 255)];
};

/**
 * Apply an OOXML tint to a base hex colour by modulating HSL luminance
 * (tint<0 darkens, tint>0 lightens). This is the widely-used normalised-HSL
 * approximation of the ECMA-376 tint algorithm — visually matches Excel.
 * Exported for unit tests.
 */
export const applyTint = (hex: string, tint?: number): string => {
    if (!tint) return hex;
    const m = /^#?([0-9A-Fa-f]{6})$/.exec(hex);
    if (!m) return hex;
    const n = parseInt(m[1], 16);
    // eslint-disable-next-line no-bitwise
    const [r, g, b] = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
    const [h, s, l0] = rgbToHsl(r, g, b);
    const l = tint < 0 ? l0 * (1 + tint) : l0 * (1 - tint) + tint;
    const [nr, ng, nb] = hslToRgb(h, s, Math.max(0, Math.min(1, l)));
    // eslint-disable-next-line no-bitwise
    return `#${((1 << 24) + (nr << 16) + (ng << 8) + nb).toString(16).slice(1).toUpperCase()}`;
};

/**
 * Resolve any exceljs colour to a hex string. Handles explicit argb and
 * theme+tint (against the workbook palette). Legacy {indexed} colours are not
 * resolved (rare; returns undefined, same as before). Exported for unit tests.
 */
export const resolveColor = (color: ExcelColor | undefined, palette: string[]): string | undefined => {
    if (!color) return undefined;
    if (color.argb) return argbToHex(color.argb);
    if (typeof color.theme === 'number') {
        const base = palette[color.theme];
        return base ? applyTint(base, color.tint) : undefined;
    }
    return undefined;
};

const MS_PER_DAY = 86_400_000;

// Excel/exceljs date serials count days since 1899-12-30 (the 1900 date system;
// the rarer 1904 system isn't handled — not something this editor's users hit).
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const dateToSerial = (d: Date): number => (d.getTime() - EXCEL_EPOCH_MS) / MS_PER_DAY;

/** The inverse of `dateToSerial` — turns a date cell's serial back into real
 *  epoch ms. Exported for sheetActions.ts's copyAbove, which needs this for
 *  its own ISO-date fallback. */
export const serialToMs = (serial: number): number => EXCEL_EPOCH_MS + serial * MS_PER_DAY;

/** Flatten an exceljs cell value (formula/error/richText/hyperlink/date wrapper) to a primitive. */
// `cachedResult` is the cell's own `cell.result`: exceljs copies a formula's `result`
// into `cell.value` only when truthy (FormulaValue._copyModel), so a cached 0, FALSE
// or "" would otherwise read as "no cached value" and be recalculated on open.
const resolveExceljsValue = (
    raw: ExcelJS.CellValue,
    cachedResult?: ExcelJS.CellValue,
): { value: string | number | boolean; isError?: boolean } | undefined => {
    if (raw == null) return undefined;
    // exceljs date-converts ANY formula result under a date number format, so an
    // error or text result arrives as an Invalid Date: treat it as "no cached value".
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? undefined : { value: dateToSerial(raw) };
    if (typeof raw === 'object') {
        if ('error' in raw) return { value: raw.error, isError: true };
        if ('richText' in raw) return { value: raw.richText.map((rt) => rt.text).join('') };
        if ('hyperlink' in raw) return { value: raw.text };
        if ('formula' in raw || 'sharedFormula' in raw) {
            const result = raw.result ?? cachedResult;
            return result != null ? resolveExceljsValue(result as ExcelJS.CellValue) : undefined;
        }
        return undefined;
    }
    return { value: raw };
};

// Map an exceljs run/cell font onto Fortune-Sheet run style keys. Skips #000000
// (the default) so black runs don't carry a redundant colour.
const fontToRunStyle = (font: Partial<ExcelJS.Font> | undefined, palette: string[]): Record<string, unknown> => {
    const s: Record<string, unknown> = {};
    if (!font) return s;
    if (font.bold) s.bl = 1;
    if (font.italic) s.it = 1;
    if (font.underline && font.underline !== 'none') s.un = 1;
    if (font.strike) s.cl = 1;
    if (font.name) s.ff = font.name;
    if (font.size) s.fs = font.size;
    const fc = resolveColor(font.color, palette);
    if (fc && fc !== '#000000') s.fc = fc;
    return s;
};

const assignFcellValue = (fcell: FortuneCell, cell: ExcelJS.Cell, palette: string[]): void => {
    const raw = cell.value;
    if (raw != null && typeof raw === 'object' && !(raw instanceof Date)) {
        if ('formula' in raw) fcell.f = `=${raw.formula}`;
        // A shared-formula child carries only `sharedFormula` in cell.value; its
        // translated text lives on the Cell getter (FormulaValue._getTranslatedFormula).
        else if ('sharedFormula' in raw && cell.formula) fcell.f = `=${cell.formula}`;
    }

    // Rich text: preserve per-run formatting as Fortune-Sheet inline-string runs
    // (ct.t='inlineStr', ct.s=[{v,...style}]) instead of flattening to one string.
    if (raw != null && typeof raw === 'object' && !(raw instanceof Date) && 'richText' in raw && Array.isArray(raw.richText)) {
        const text = raw.richText.map((rt) => rt.text).join('');
        fcell.v = text;
        fcell.m = text;
        fcell.ct = { t: 'inlineStr', fa: 'General', s: raw.richText.map((rt) => ({ v: rt.text, ...fontToRunStyle(rt.font, palette) })) };
        const richHt = defaultHtForType('s');
        if (richHt !== undefined) fcell.ht = richHt;
        return;
    }

    const resolved = resolveExceljsValue(raw, cell.result as ExcelJS.CellValue);
    if (!resolved) {
        // Formula saved without a cached result (e.g. openpyxl): no `v` to invent, but the
        // number format must survive so the recalculated value renders and exports formatted.
        if (fcell.f && cell.numFmt) {
            fcell.ct = { t: 'n', fa: isFormattableNumFmt(cell.numFmt) ? numFmt(cell.numFmt) : 'General' };
            fcell.ht = defaultHtForType('n');
        }
        return;
    }
    const { value, isError } = resolved;
    if (isError) {
        fcell.v = value as string;
        fcell.m = value as string;
        fcell.ct = { t: 'e', fa: 'General' };
    } else if (typeof value === 'boolean') {
        fcell.v = value;
        fcell.m = value ? 'TRUE' : 'FALSE';
        fcell.ct = { t: 'b', fa: 'General' };
    } else if (typeof value === 'number') {
        fcell.v = value;
        let formatted: string;
        let fa = numFmt(cell.numFmt);
        try {
            formatted = cell.numFmt ? XLSX.SSF.format(cell.numFmt, value) : String(value);
        } catch {
            // SSF.format can throw on number-format strings ExcelJS reads verbatim off the
            // source xlsx's XML but SheetJS's SSF doesn't support (e.g. accounting/locale
            // formats) — fall back to the raw value rather than aborting the whole parse.
            // Also drop the format from ct.fa (not just `formatted`/`m`): Fortune-Sheet can
            // recompute m from ct.fa on later edits/reformats, which would re-throw on the
            // same unsupported string; General keeps the cell editable and safe to re-export.
            console.warn('[SheetEditor] unparseable number format, falling back to raw value', cell.numFmt);
            formatted = typeof cell.numFmt === 'string' && cell.numFmt.includes('%') ? percentFallback(value) : String(value);
            fa = 'General';
        }
        fcell.m = formatted;
        fcell.ct = { t: 'n', fa };
    } else {
        fcell.v = value;
        fcell.m = value;
        fcell.ct = { t: 's', fa: '@' };
    }
    // Type-based default alignment. Runs BEFORE applyExceljsStyle (called by the
    // caller right after), so an explicit Excel alignment.horizontal still wins.
    const ht = defaultHtForType(fcell.ct?.t);
    if (ht !== undefined) fcell.ht = ht;
};

/** Map exceljs font/fill/alignment onto a Fortune-Sheet cell. Only sets present fields. */
const applyExceljsStyle = (fcell: FortuneCell, cell: ExcelJS.Cell, palette: string[]): void => {
    const font = cell.font;
    if (font?.bold) fcell.bl = 1;
    if (font?.italic) fcell.it = 1;
    if (font?.underline && font.underline !== 'none') fcell.un = 1;
    if (font?.strike) fcell.cl = 1;
    if (font?.name) fcell.ff = font.name;
    if (font?.size) fcell.fs = font.size;
    const fc = resolveColor(font?.color, palette);
    // Skip #000000 — it's the editor's default text colour, and Excel marks
    // ordinary black text with theme:1 (dk1) on essentially every cell. Setting
    // it explicitly would add a redundant fc to hundreds of thousands of cells.
    if (fc && fc !== '#000000') fcell.fc = fc;

    const fill = cell.fill;
    if (fill?.type === 'pattern' && fill.pattern === 'solid') {
        const bg = resolveColor(fill.fgColor, palette);
        if (bg) fcell.bg = bg;
    }

    const alignment = cell.alignment;
    if (alignment?.horizontal === 'left') fcell.ht = 1;
    else if (alignment?.horizontal === 'center') fcell.ht = 0;
    else if (alignment?.horizontal === 'right') fcell.ht = 2;
    if (alignment?.vertical === 'top') fcell.vt = 1;
    else if (alignment?.vertical === 'middle') fcell.vt = 0;
    else if (alignment?.vertical === 'bottom') fcell.vt = 2;
    if (alignment?.wrapText) fcell.tb = '2';
    // Unwrapped text spills into empty neighbours like Excel; Fortune only spills on tb '1'.
    else if (fcell.ct?.t === 's' || fcell.ct?.t === 'inlineStr') fcell.tb = '1';
};

const BORDER_SIDES = [
    ['top', 't'],
    ['left', 'l'],
    ['bottom', 'b'],
    ['right', 'r'],
] as const;

/** Collect a cell's borders into a Fortune-Sheet config.borderInfo entry, or undefined if none. */
const collectBorderEntry = (
    row_index: number,
    col_index: number,
    border: Partial<ExcelJS.Borders> | undefined,
    palette: string[],
): BorderCellEntry | undefined => {
    if (!border) return undefined;
    const value: BorderCellEntry['value'] = { row_index, col_index };
    let any = false;
    BORDER_SIDES.forEach(([side, key]) => {
        const b = border[side];
        const code = b?.style ? BORDER_STYLE_TO_CODE[b.style] : undefined;
        if (!code) return;
        value[key] = { style: code, color: resolveColor(b?.color, palette) ?? '#000000' };
        any = true;
    });
    return any ? { rangeType: 'cell', value } : undefined;
};

type MergeMaster = { r: number; c: number; rs: number; cs: number };

// Exported for unit tests (real-bytes import tests are CI-skipped — see converters.test.ts).
export const exceljsWorksheetToCelldata = (
    ws: ExcelJS.Worksheet,
    palette: string[] = OFFICE_THEME_PALETTE,
): {
    celldata: FortuneCellData[];
    rows: number;
    cols: number;
    config: FortuneSheet['config'];
    defaultRowHeight?: number;
    defaultColWidth?: number;
    hyperlink?: Record<string, { linkType: string; linkAddress: string }>;
} => {
    const celldata: FortuneCellData[] = [];
    const borderInfo: BorderCellEntry[] = [];
    const rowlen: Record<string, number> = {};
    // The counterpart of addExceljsWorksheet's own rowhidden/colhidden export
    // — 0 (not a meaningful value) matches Fortune's own hideSelected
    // convention (core:66838-66847), which only ever checks key presence.
    const rowhidden: Record<string, number> = {};
    const colhidden: Record<string, number> = {};
    const hyperlink: Record<string, { linkType: string; linkAddress: string }> = {};
    let maxR = 0;
    let maxC = 0;

    const config: NonNullable<FortuneSheet['config']> = {};
    // FortuneSheet renders a merge's rowspan/colspan off the CELL's own `mc` field, not
    // config.merge — so every cell in a merge range needs `mc` (master: full {r,c,rs,cs};
    // covered: just the master's {r,c}), including cells `eachCell({includeEmpty:false})`
    // would otherwise skip because they carry no value.
    const masterMc = new Map<string, MergeMaster>();
    const coveredMc = new Map<string, { r: number; c: number }>();
    const merges = ws.model.merges;
    if (merges?.length) {
        config.merge = {};
        merges.forEach((rangeStr) => {
            const { s, e } = XLSX.utils.decode_range(rangeStr);
            const master: MergeMaster = { r: s.r, c: s.c, rs: e.r - s.r + 1, cs: e.c - s.c + 1 };
            config.merge![`${s.r}_${s.c}`] = master;
            masterMc.set(`${s.r}_${s.c}`, master);
            // A merge can reach past the last valued cell — extend the sheet bounds to fit.
            maxR = Math.max(maxR, e.r);
            maxC = Math.max(maxC, e.c);
            for (let rr = s.r; rr <= e.r; rr++) {
                for (let cc = s.c; cc <= e.c; cc++) {
                    if (rr === s.r && cc === s.c) continue;
                    coveredMc.set(`${rr}_${cc}`, { r: s.r, c: s.c });
                }
            }
        });
    }

    const emitted = new Set<string>();

    // includeEmpty:true so a row's custom height is collected even when the row has
    // no cells at all (eachCell below still skips empty cells for cell-value purposes).
    ws.eachRow({ includeEmpty: true }, (row, rn) => {
        // Clamp UP to the floor — a shorter authored row height (e.g. from a
        // dense source workbook) must not render shorter than MIN_ROW_HEIGHT.
        if (row.height != null) rowlen[String(rn - 1)] = Math.max(Math.round((row.height * 96) / 72), MIN_ROW_HEIGHT);
        if (row.hidden) rowhidden[String(rn - 1)] = 0;

        row.eachCell({ includeEmpty: false }, (cell, cn) => {
            const r = rn - 1;
            const c = cn - 1;
            const key = `${r}_${c}`;

            // exceljs proxies the MASTER's value onto every slave cell in a merge
            // (cell.value/isMerged both report as if the slave held the master's
            // content) — reading it as the slave's own value would duplicate the
            // master's text across the whole merge. A slave carries `mc` only.
            if (cell.isMerged && cell.master !== cell) {
                const covered = coveredMc.get(key);
                if (covered) {
                    celldata.push({ r, c, v: { mc: covered } });
                    emitted.add(key);
                }
                return;
            }

            const fcell: FortuneCell = {};
            assignFcellValue(fcell, cell, palette);
            if (fcell.v === undefined && !fcell.f) return;

            applyExceljsStyle(fcell, cell, palette);
            const border = collectBorderEntry(r, c, cell.border, palette);
            if (border) borderInfo.push(border);

            // Hyperlink: exceljs exposes it either as a {text,hyperlink} value or a
            // cell.hyperlink property. Store the URL in the sheet-level map keyed
            // "row_col" (Fortune-Sheet's model); the cell keeps its display text.
            const rawVal = cell.value;
            const url =
                rawVal != null && typeof rawVal === 'object' && !(rawVal instanceof Date) && 'hyperlink' in rawVal
                    ? (rawVal as { hyperlink?: string }).hyperlink
                    : (cell.hyperlink as string | undefined);
            if (url) hyperlink[`${r}_${c}`] = { linkType: 'webpage', linkAddress: url };

            const master = masterMc.get(key);
            if (master) fcell.mc = master;

            maxR = Math.max(maxR, r);
            maxC = Math.max(maxC, c);
            celldata.push({ r, c, v: fcell });
            emitted.add(key);
        });
    });

    // Backfill merge master/covered cells eachCell skipped (no value of their own) —
    // an empty merged block still needs its master (and every covered cell) present.
    masterMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        celldata.push({ r: master.r, c: master.c, v: { mc: master } });
    });
    coveredMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        const [r, c] = key.split('_').map(Number);
        celldata.push({ r, c, v: { mc: master } });
    });

    // Aggregate uniform per-cell borders into 'range' entries and cap the rest —
    // Fortune-Sheet iterates every 'cell' borderInfo entry on each repaint, so a
    // heavily-bordered workbook (one entry per cell) stalls all scrolling.
    if (borderInfo.length) config.borderInfo = aggregateBorderInfo(borderInfo);
    if (Object.keys(rowlen).length) config.rowlen = rowlen;
    if (Object.keys(rowhidden).length) config.rowhidden = rowhidden;

    const columnlen: Record<string, number> = {};
    // exceljs types columns as Column[], but the getter returns its internal
    // _columns, which is null for sheets with no <cols> block (empty divider tabs).
    (ws.columns ?? []).forEach((col, i) => {
        if (col?.width != null) columnlen[String(i)] = Math.round(col.width * 7);
        if (col?.hidden) colhidden[String(i)] = 0;
    });
    if (Object.keys(columnlen).length) config.columnlen = columnlen;
    if (Object.keys(colhidden).length) config.colhidden = colhidden;

    const defaultRowHeight = ws.properties?.defaultRowHeight != null ? Math.round((ws.properties.defaultRowHeight * 96) / 72) : undefined;
    const defaultColWidth = ws.properties?.defaultColWidth != null ? Math.round(ws.properties.defaultColWidth * 7) : undefined;

    return {
        celldata,
        rows: Math.max(MIN_ROWS, maxR + 1),
        cols: Math.max(MIN_COLS, maxC + 1),
        config,
        defaultRowHeight,
        defaultColWidth,
        ...(Object.keys(hyperlink).length ? { hyperlink } : {}),
    };
};

const parseXlsxAsync = async (buffer: ArrayBuffer): Promise<FortuneSheet[]> => {
    const wb = new ExcelJS.Workbook();
    // exceljs's own .d.ts types this as Node's Buffer, but its browser build (and this
    // Node runtime) both accept a plain ArrayBuffer directly — verified empirically.
    await wb.xlsx.load(buffer as unknown as Buffer);
    // Resolve the workbook theme palette once so {theme,tint} colours become hex.
    const palette = parseThemePalette((wb.model as unknown as { themes?: Record<string, string> })?.themes?.theme1);
    return wb.worksheets.map((ws, i) => {
        const { celldata, rows, cols, config, defaultRowHeight, defaultColWidth, hyperlink } = exceljsWorksheetToCelldata(ws, palette);
        const sheet = buildSheet(ws.name, i, celldata, rows, cols, config, defaultRowHeight, defaultColWidth);
        if (hyperlink) (sheet as FortuneSheet & { hyperlink?: Record<string, unknown> }).hyperlink = hyperlink;
        return sheet;
    });
};

/**
 * Parse fetched file bytes into Fortune-Sheet sheets. Detects XLSX by content
 * (ZIP magic bytes) so a mislabelled .csv that is really an .xlsx still opens,
 * mirroring DocumentViewer's detection. When the MIME type doesn't confirm CSV
 * (empty/wrong — common for local file pickers on some OS/browsers), fall back
 * to the file's extension before defaulting to XLSX, so a real local .csv with
 * a blank/incorrect `type` doesn't get mis-parsed as a workbook.
 *
 * A legacy binary .xls (OLE2 magic) or a text-shaped .xls variant (HTML-table,
 * SpreadsheetML-2003 — named `.xls` AND leading with markup) routes to SheetJS
 * via parseLegacyXls instead of falling through to exceljs, which is
 * OOXML/ZIP-only and throws on all of these. The extension half of that gate
 * is content-checked (leadsToMarkup), not bare — XLSX.read DSV-parses
 * non-spreadsheet bytes (a PDF, plaintext) into a fake "successful" workbook
 * instead of throwing, so a bare `ext === 'xls'` gate would turn today's
 * honest error on a mislabelled file into a silent junk grid.
 */
/**
 * Whether `buffer` routes to the legacy-.xls (SheetJS) parser rather than
 * exceljs (ZIP/OOXML, full style fidelity) or CSV — the single source of
 * truth `fileBytesToFortuneSheets` itself consumes below, so any caller
 * (e.g. the legacy-.xls notice) shares its notion of "legacy" by
 * construction. Reads at most the first ~1KB of the buffer (the same bound
 * as `leadsToMarkup`), so a caller may pass a slice without affecting
 * correctness.
 */
export const isLegacyXlsRoute = (buffer: ArrayBuffer, mimeType?: string, fileName?: string): boolean => {
    if (isXlsxBuffer(buffer)) return false; // ZIP wins — A-09
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType === 'text/csv' || mimeType === 'application/csv' || ext === 'csv') return false;
    return isLegacyXlsBuffer(buffer) || (ext === 'xls' && leadsToMarkup(buffer));
};

export const fileBytesToFortuneSheets = async (buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<FortuneSheet[]> => {
    if (isXlsxBuffer(buffer)) return parseXlsxAsync(buffer);
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType === 'text/csv' || mimeType === 'application/csv' || ext === 'csv') return parseCsv(decodeText(buffer));
    if (isLegacyXlsRoute(buffer, mimeType, fileName)) return parseLegacyXls(buffer);
    return parseXlsxAsync(buffer);
};

/**
 * Read a File object into FortuneSheet[]. Use this in the import flow
 * (xlsx/xls/csv) to go from a user-selected file to sheets ready for
 * SheetEditorDrawer's `sheets` prop.
 */
export const importFileToSheets = async (file: File): Promise<FortuneSheet[]> => {
    const buffer = await file.arrayBuffer();
    return fileBytesToFortuneSheets(buffer, file.type, file.name);
};

/** Normalise a Fortune-Sheet sheet (celldata or 2D data) to a flat cell list. */
const getCells = (sheet: FortuneSheet): FortuneCellData[] => {
    if (sheet.celldata && sheet.celldata.length) return sheet.celldata;
    if (sheet.data) {
        const out: FortuneCellData[] = [];
        sheet.data.forEach((row, r) => {
            row?.forEach((cell, c) => {
                if (cell == null) return;
                out.push({ r, c, v: cell });
            });
        });
        return out;
    }
    return [];
};

/**
 * Normalise a sheet to Fortune-Sheet's `celldata` init form. `<Workbook data>`
 * builds the grid from `celldata`, but `getAllSheets()` (what save persists)
 * returns only the runtime 2D `data` plus selection state — so re-feeding a
 * saved sheet to the editor renders an empty grid. Rebuild `celldata` from
 * `data`, drop the runtime-only `data`/`luckysheet_select_save`, and keep
 * everything else (`config` merges/widths/styles, `frozen`, …). No-op for
 * sheets that already carry `celldata` (the import path).
 */
export const toCelldataSheet = (sheet: FortuneSheet): FortuneSheet => {
    const { data, luckysheet_select_save, ...rest } = sheet;
    return { ...rest, celldata: getCells(sheet) };
};

const cellRaw = (v: FortuneCellData['v']): { value: string | number | boolean | null; formula?: string } => {
    if (v == null) return { value: null };
    if (typeof v === 'object') return { value: v.v ?? null, formula: v.f };
    return { value: v };
};

// Fortune's setCellValue (core, General branch) stores a typed number as its raw
// string ("5") with ct.t 'n'; exceljs would write that as a shared string. Text-format
// cells (fa '@', incl. the apostrophe prefix) and non-'n' cells keep their string.
const DECIMAL_LITERAL = /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;

export const exportScalar = (
    value: string | number | boolean | null,
    ct: FortuneCell['ct'] | undefined,
): string | number | boolean | null => {
    if (typeof value !== 'string' || ct?.t !== 'n' || ct.fa === '@' || !DECIMAL_LITERAL.test(value)) return value;
    const num = Number(value);
    return Number.isFinite(num) ? num : value;
};

// ============================================================================
// .xlsx EXPORT — exceljs (full font/fill/alignment/border write fidelity).
// ============================================================================

const buildExceljsFont = (v: FortuneCell): Partial<ExcelJS.Font> | undefined => {
    const font: Partial<ExcelJS.Font> = {};
    if (v.bl) font.bold = true;
    if (v.it) font.italic = true;
    if (v.un) font.underline = true;
    if (v.cl) font.strike = true;
    if (v.ff != null) font.name = String(v.ff);
    if (v.fs != null) font.size = v.fs;
    const argb = hexToArgb(v.fc);
    if (argb) font.color = { argb };
    return Object.keys(font).length ? font : undefined;
};

const buildExceljsFill = (v: FortuneCell): ExcelJS.Fill | undefined => {
    const argb = hexToArgb(v.bg);
    return argb ? { type: 'pattern', pattern: 'solid', fgColor: { argb } } : undefined;
};

// Fortune's toolbar writes `ht`/`vt`/`tr` as STRINGS (core:68481,
// :68489, :68507 map "left"/"center"/"right" etc. to "1"/"0"/"2", written at
// :68532) even though the library's own type declares `ht`/`vt` as `number` —
// only the importer (converters.ts's own writer) stores numbers. Comparing
// with `===` against a number therefore silently dropped every explicit
// toolbar alignment on export (pre-existing on origin/main); `Number(...)`
// normalises both shapes.
const TEXT_ROTATION_MAP: Record<string, ExcelJS.Alignment['textRotation']> = {
    '1': 45,
    '2': -45,
    '3': 'vertical',
    '4': 90,
    '5': -90,
};

// '' (and any other non-numeric string) means unset, not 0 — `Number('')` is
// 0, which would otherwise read as an explicit centre/middle alignment. `raw`
// is typed `number` (Fortune's own declaration) but, per the comment above,
// is a string at runtime.
const toAlignmentCode = (raw: number | undefined): number | null => {
    if (raw == null || String(raw) === '') return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
};

const buildExceljsAlignment = (v: FortuneCell): Partial<ExcelJS.Alignment> | undefined => {
    const alignment: Partial<ExcelJS.Alignment> = {};
    const ht = toAlignmentCode(v.ht);
    const vt = toAlignmentCode(v.vt);
    if (ht === 1) alignment.horizontal = 'left';
    else if (ht === 0) alignment.horizontal = 'center';
    else if (ht === 2) alignment.horizontal = 'right';
    if (vt === 1) alignment.vertical = 'top';
    else if (vt === 0) alignment.vertical = 'middle';
    else if (vt === 2) alignment.vertical = 'bottom';
    if (v.tb === '2') alignment.wrapText = true;
    const textRotation = v.tr != null ? TEXT_ROTATION_MAP[v.tr] : undefined;
    if (textRotation !== undefined) alignment.textRotation = textRotation;
    return Object.keys(alignment).length ? alignment : undefined;
};

const BORDER_TYPE_TO_SIDES: Record<string, ('left' | 'right' | 'top' | 'bottom')[]> = {
    'border-all': ['left', 'right', 'top', 'bottom'],
    'border-top': ['top'],
    'border-bottom': ['bottom'],
    'border-left': ['left'],
    'border-right': ['right'],
};

// Handles rangeType 'cell' entries plus the 'range' entries (border-all and
// per-side border-top/bottom/left/right) our own import aggregation produces.
const buildBorderLookup = (borderInfo: unknown): Map<string, Partial<ExcelJS.Borders>> => {
    const map = new Map<string, Partial<ExcelJS.Borders>>();

    const mergeSides = (key: string, sides: Partial<ExcelJS.Borders>): void => {
        const existing = map.get(key);
        map.set(key, existing ? { ...existing, ...sides } : sides);
    };

    ((borderInfo as BorderInfoEntry[] | undefined) ?? []).forEach((entry) => {
        if (entry?.rangeType === 'range') {
            const sideNames = BORDER_TYPE_TO_SIDES[entry.borderType];
            if (!sideNames) return;
            const side = { style: BORDER_CODE_TO_STYLE[entry.style], color: { argb: hexToArgb(entry.color) } };
            const sides: Partial<ExcelJS.Borders> = {};
            sideNames.forEach((s) => {
                sides[s] = side;
            });
            (entry.range ?? []).forEach(({ row, column }) => {
                for (let r = row[0]; r <= row[1]; r++) {
                    for (let c = column[0]; c <= column[1]; c++) {
                        mergeSides(`${r}_${c}`, sides);
                    }
                }
            });
            return;
        }
        if (entry?.rangeType !== 'cell' || !entry.value) return;
        const { row_index, col_index, l, r, t, b } = entry.value;
        const sides: Partial<ExcelJS.Borders> = {};
        if (l) sides.left = { style: BORDER_CODE_TO_STYLE[l.style], color: { argb: hexToArgb(l.color) } };
        if (r) sides.right = { style: BORDER_CODE_TO_STYLE[r.style], color: { argb: hexToArgb(r.color) } };
        if (t) sides.top = { style: BORDER_CODE_TO_STYLE[t.style], color: { argb: hexToArgb(t.color) } };
        if (b) sides.bottom = { style: BORDER_CODE_TO_STYLE[b.style], color: { argb: hexToArgb(b.color) } };
        if (Object.keys(sides).length) mergeSides(`${row_index}_${col_index}`, sides);
    });
    return map;
};

/** Excel's default row height, in points. */
const DEFAULT_ROW_HEIGHT_PT = 15;

const addExceljsWorksheet = (wb: ExcelJS.Workbook, sheet: FortuneSheet, name: string): void => {
    // Write a sheet-level default width back (px -> Excel character units) so unsized columns reopen as imported.
    const ws = wb.addWorksheet(
        name,
        sheet.defaultColWidth
            ? ({ properties: { defaultColWidth: sheet.defaultColWidth / 7 } } as Partial<ExcelJS.AddWorksheetOptions>)
            : undefined,
    );
    const borders = buildBorderLookup(sheet.config?.borderInfo);
    const hyperlinks = (sheet as FortuneSheet & { hyperlink?: Record<string, { linkType: string; linkAddress: string }> }).hyperlink ?? {};

    getCells(sheet).forEach(({ r, c, v }) => {
        const { value, formula } = cellRaw(v);
        const fcell = v && typeof v === 'object' ? (v as FortuneCell) : undefined;
        const link = hyperlinks[`${r}_${c}`];
        const richRuns = fcell?.ct?.t === 'inlineStr' && Array.isArray(fcell.ct.s) ? (fcell.ct.s as Record<string, unknown>[]) : undefined;
        if (value == null && !formula && !link && !richRuns) return;

        const xc = ws.getCell(r + 1, c + 1);
        if (formula) {
            xc.value = { formula: formula.replace(/^=/, ''), result: value ?? undefined };
        } else if (link) {
            // Round-trip the hyperlink as an exceljs {text, hyperlink} value.
            xc.value = { text: value == null ? '' : String(value), hyperlink: link.linkAddress };
        } else if (richRuns) {
            // Round-trip per-run formatting from ct.s back into exceljs richText.
            xc.value = {
                richText: richRuns.map((run) => ({ text: run.v == null ? '' : String(run.v), font: buildExceljsFont(run as FortuneCell) })),
            };
        } else {
            xc.value = exportScalar(value, fcell?.ct);
        }

        // Carry the SSF number format through to the exported workbook.
        if (fcell?.ct?.fa && fcell.ct.fa !== 'General') xc.numFmt = fcell.ct.fa;

        if (fcell) {
            const font = buildExceljsFont(fcell);
            if (font) xc.font = font;
            const fill = buildExceljsFill(fcell);
            if (fill) xc.fill = fill;
            const alignment = buildExceljsAlignment(fcell);
            if (alignment) xc.alignment = alignment;
        }
        const border = borders.get(`${r}_${c}`);
        if (border) xc.border = border;
    });

    if (sheet.config?.merge) {
        Object.values(sheet.config.merge).forEach((m) => {
            ws.mergeCells(m.r + 1, m.c + 1, m.r + m.rs, m.c + m.cs);
        });
    }
    if (sheet.config?.columnlen) {
        Object.entries(sheet.config.columnlen).forEach(([i, px]) => {
            ws.getColumn(Number(i) + 1).width = px / 7;
        });
    }
    if (sheet.config?.rowlen) {
        Object.entries(sheet.config.rowlen).forEach(([i, px]) => {
            ws.getRow(Number(i) + 1).height = (px * 72) / 96;
        });
    }
    // Ctrl+9/Ctrl+0-hidden rows/columns (sheetActions.ts's hideOrShow) must
    // round-trip through an xlsx export.
    // Fortune's saveFilter merges AutoFilter-hidden rows into config.rowhidden;
    // they belong to the filter, not to a manual hide, so they stay visible here
    // (no autoFilter is written, so a hidden row would be stuck hidden).
    if (sheet.config?.rowhidden) {
        const filterHiddenRows = new Set(Object.values(sheet.filter ?? {}).flatMap((f) => Object.keys(f?.rowhidden ?? {})));
        Object.keys(sheet.config.rowhidden)
            .filter((i) => !filterHiddenRows.has(i))
            .forEach((i) => {
                const row = ws.getRow(Number(i) + 1);
                row.hidden = true;
                // exceljs writes a row only when it has cells or a height, so a hidden
                // empty row would silently come back visible.
                if (row.cellCount === 0 && !row.height) {
                    row.height = sheet.defaultRowHeight ? (sheet.defaultRowHeight * 72) / 96 : DEFAULT_ROW_HEIGHT_PT;
                }
            });
    }
    if (sheet.config?.colhidden) {
        Object.keys(sheet.config.colhidden).forEach((i) => {
            ws.getColumn(Number(i) + 1).hidden = true;
        });
    }
};

// Excel forbids these characters in sheet names and caps length at 31.
const sanitizeSheetName = (name: string, index: number): string => {
    const cleaned = (name || `Sheet${index + 1}`)
        .replace(/[\\/?*[\]:]/g, ' ')
        .slice(0, 31)
        .trim();
    return cleaned || `Sheet${index + 1}`;
};

// Excel rejects duplicate sheet names (case-insensitive); suffix collisions
// while keeping within the 31-char cap so addWorksheet never throws.
const uniqueSheetName = (base: string, used: Set<string>): string => {
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
        const suffix = ` (${n})`;
        name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
        n += 1;
    }
    used.add(name.toLowerCase());
    return name;
};

const triggerDownload = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const stripExtension = (fileName: string): string => fileName.replace(/\.(xlsx|xls|csv)$/i, '');

// Prevent CSV formula injection: a cell beginning with = + - @ can execute when
// the exported file is opened in Excel/Sheets. Prefix such strings with a quote.
const sanitizeCsvCell = (value: string | number | boolean): string | number | boolean =>
    typeof value === 'string' && /^[=+\-@]/.test(value) ? `'${value}` : value;

const XLSX_BLOB_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Serialize sheets to .xlsx bytes. DOM-free — safe to run inside a Web Worker. */
export const buildXlsxBuffer = async (sheets: FortuneSheet[]): Promise<ArrayBuffer> => {
    const wb = new ExcelJS.Workbook();
    const usedNames = new Set<string>();
    sheets.forEach((sheet, i) => {
        const name = uniqueSheetName(sanitizeSheetName(sheet.name, i), usedNames);
        addExceljsWorksheet(wb, sheet, name);
    });
    return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
};

/** Trigger a browser download for already-serialized .xlsx bytes (main thread only). */
export const downloadXlsxBuffer = (buffer: ArrayBuffer, fileName: string): void => {
    triggerDownload(new Blob([buffer], { type: XLSX_BLOB_TYPE }), `${stripExtension(fileName)}.xlsx`);
};

/** Export every sheet to a single .xlsx workbook and download it. */
export const downloadAsXlsx = async (sheets: FortuneSheet[], fileName: string): Promise<void> => {
    downloadXlsxBuffer(await buildXlsxBuffer(sheets), fileName);
};

/** Export a single sheet (the active one) to .csv and download it. */
export const downloadAsCsv = (sheet: FortuneSheet, fileName: string): void => {
    const cells = getCells(sheet);
    let maxR = 0;
    let maxC = 0;
    cells.forEach(({ r, c }) => {
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);
    });
    const aoa: (string | number | boolean)[][] = Array.from({ length: maxR + 1 }, () => Array.from({ length: maxC + 1 }, () => ''));
    cells.forEach(({ r, c, v }) => {
        // Use the raw value, not the display string `m` — for number-formatted
        // cells `m` carries separators/currency (e.g. "1,234") which corrupts CSV.
        const { value } = cellRaw(v);
        aoa[r][c] = sanitizeCsvCell((value ?? '') as string | number | boolean);
    });
    const csv = Papa.unparse(aoa);
    triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${stripExtension(fileName)}.csv`);
};
